import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from "react-leaflet";
import { ArrowUpRight, Search, MapPin } from "lucide-react";
import "leaflet/dist/leaflet.css";

/**
 * Real interactive Leaflet map of the Netherlands with city search.
 * Clicking a marker (or a search result) pre-fills the wachtlijst form via a
 * querystring, so the receiving Contact page can auto-fill.
 *
 * Data: CBS StatLine 82015NED, indicative per-city numbers, december 2024.
 * These are richcijfers, niet exact per gemeente.
 */
const steden = [
  { naam: "Amsterdam", lat: 52.3676, lng: 4.9041, bijstand: 40100, prov: "Noord-Holland" },
  { naam: "Rotterdam", lat: 51.9244, lng: 4.4777, bijstand: 33900, prov: "Zuid-Holland" },
  { naam: "Den Haag", lat: 52.0705, lng: 4.3007, bijstand: 25600, prov: "Zuid-Holland" },
  { naam: "Utrecht", lat: 52.0907, lng: 5.1214, bijstand: 8700, prov: "Utrecht" },
  { naam: "Eindhoven", lat: 51.4416, lng: 5.4697, bijstand: 6900, prov: "Noord-Brabant" },
  { naam: "Groningen", lat: 53.2194, lng: 6.5665, bijstand: 8100, prov: "Groningen" },
  { naam: "Tilburg", lat: 51.5555, lng: 5.0913, bijstand: 6300, prov: "Noord-Brabant" },
  { naam: "Almere", lat: 52.3508, lng: 5.2647, bijstand: 5100, prov: "Flevoland" },
  { naam: "Breda", lat: 51.5719, lng: 4.7683, bijstand: 3800, prov: "Noord-Brabant" },
  { naam: "Nijmegen", lat: 51.8126, lng: 5.8372, bijstand: 4200, prov: "Gelderland" },
  { naam: "Enschede", lat: 52.2215, lng: 6.8937, bijstand: 6200, prov: "Overijssel" },
  { naam: "Haarlem", lat: 52.3874, lng: 4.6462, bijstand: 3300, prov: "Noord-Holland" },
  { naam: "Arnhem", lat: 51.9851, lng: 5.8987, bijstand: 5100, prov: "Gelderland" },
  { naam: "Zaanstad", lat: 52.4574, lng: 4.7511, bijstand: 3900, prov: "Noord-Holland" },
  { naam: "Amersfoort", lat: 52.1561, lng: 5.3878, bijstand: 2600, prov: "Utrecht" },
  { naam: "'s-Hertogenbosch", lat: 51.6978, lng: 5.3037, bijstand: 3100, prov: "Noord-Brabant" },
  { naam: "Apeldoorn", lat: 52.2112, lng: 5.9699, bijstand: 3300, prov: "Gelderland" },
  { naam: "Dordrecht", lat: 51.8133, lng: 4.6901, bijstand: 3400, prov: "Zuid-Holland" },
  { naam: "Leiden", lat: 52.1601, lng: 4.4970, bijstand: 3000, prov: "Zuid-Holland" },
  { naam: "Zwolle", lat: 52.5168, lng: 6.0830, bijstand: 2700, prov: "Overijssel" },
  { naam: "Maastricht", lat: 50.8514, lng: 5.6910, bijstand: 3900, prov: "Limburg" },
  { naam: "Delft", lat: 52.0116, lng: 4.3571, bijstand: 2300, prov: "Zuid-Holland" },
  { naam: "Leeuwarden", lat: 53.2012, lng: 5.7999, bijstand: 4400, prov: "Friesland" },
  { naam: "Alkmaar", lat: 52.6324, lng: 4.7534, bijstand: 2800, prov: "Noord-Holland" },
  { naam: "Emmen", lat: 52.7859, lng: 6.8974, bijstand: 3500, prov: "Drenthe" },
  { naam: "Venlo", lat: 51.3704, lng: 6.1724, bijstand: 3300, prov: "Limburg" },
  { naam: "Deventer", lat: 52.2661, lng: 6.1552, bijstand: 3600, prov: "Overijssel" },
  { naam: "Sittard", lat: 51.0018, lng: 5.8688, bijstand: 3100, prov: "Limburg" },
  { naam: "Heerlen", lat: 50.8882, lng: 5.9795, bijstand: 4900, prov: "Limburg" },
  { naam: "Helmond", lat: 51.4816, lng: 5.6612, bijstand: 2900, prov: "Noord-Brabant" },
  { naam: "Gouda", lat: 52.0116, lng: 4.7106, bijstand: 2000, prov: "Zuid-Holland" },
  { naam: "Middelburg", lat: 51.4988, lng: 3.6106, bijstand: 1200, prov: "Zeeland" },
  { naam: "Assen", lat: 52.9967, lng: 6.5625, bijstand: 1900, prov: "Drenthe" },
  { naam: "Lelystad", lat: 52.5185, lng: 5.4714, bijstand: 4100, prov: "Flevoland" },
];

function fmt(n) {
  return n.toLocaleString("nl-NL");
}

function FlyTo({ target }) {
  const map = useMap();
  useEffect(() => {
    if (!target) return;
    map.flyTo([target.lat, target.lng], 10, { duration: 1.4 });
  }, [target, map]);
  return null;
}

export default function LeafletKaart() {
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [flyTarget, setFlyTarget] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const inputRef = useRef(null);

  const results = useMemo(() => {
    if (!q.trim()) return [];
    const s = q.trim().toLowerCase();
    return steden
      .filter((c) => c.naam.toLowerCase().includes(s) || c.prov.toLowerCase().includes(s))
      .slice(0, 6);
  }, [q]);

  const pickCity = (c) => {
    setSelected(c);
    setFlyTarget(c);
    setQ(c.naam);
    // collapse suggestions after pick
    setTimeout(() => inputRef.current?.blur(), 50);
    setShowResults(false);
  };

  const max = Math.max(...steden.map((c) => c.bijstand));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
      {/* MAP */}
      <div className="lg:col-span-8 relative" data-testid="leaflet-map-wrap">
        <div className="relative rounded-3xl overflow-hidden border border-[#E5E1D8] bg-[#EFECE5]">
          <MapContainer
            center={[52.2, 5.4]}
            zoom={7}
            scrollWheelZoom={false}
            style={{ height: "620px", width: "100%" }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FlyTo target={flyTarget} />
            {steden.map((c) => {
              const t = c.bijstand / max;
              const r = 6 + t * 18;
              return (
                <CircleMarker
                  key={c.naam}
                  center={[c.lat, c.lng]}
                  radius={r}
                  pathOptions={{
                    color: "#E06B3F",
                    fillColor: "#E06B3F",
                    fillOpacity: 0.35 + t * 0.35,
                    weight: 1.5,
                  }}
                  eventHandlers={{ click: () => setSelected(c) }}
                >
                  <Popup>
                    <div className="font-heading text-lg text-[#1D2A22]">{c.naam}</div>
                    <div className="text-xs text-[#7A8276] mt-0.5">{c.prov}</div>
                    <div className="mt-2 text-sm">
                      <span className="text-[#E06B3F] font-medium">{fmt(c.bijstand)}</span>{" "}
                      bijstandsuitkeringen
                    </div>
                    <Link
                      to={`/contact?rol=wachtlijst&stad=${encodeURIComponent(c.naam)}&provincie=${encodeURIComponent(c.prov)}`}
                      className="mt-3 inline-block text-xs text-[#E06B3F] underline"
                    >
                      Plaats mij op de wachtlijst →
                    </Link>
                  </Popup>
                </CircleMarker>
              );
            })}
          </MapContainer>

          {/* Floating search, top-right so Leaflet zoom controls stay usable */}
          <div className="absolute top-4 right-4 left-4 sm:left-auto sm:w-[320px] z-[500]">
            <div className="bg-[#FAF7F1]/95 backdrop-blur-md border border-[#E5E1D8] rounded-2xl shadow-lg overflow-hidden">
              <label className="flex items-center gap-2 px-4 py-3">
                <Search size={16} className="text-[#7A8276]" />
                <input
                  ref={inputRef}
                  data-testid="kaart-zoek-input"
                  value={q}
                  onChange={(e) => {
                    setQ(e.target.value);
                    setShowResults(true);
                  }}
                  onFocus={() => setShowResults(true)}
                  placeholder="Zoek stad of provincie…"
                  className="w-full bg-transparent outline-none text-sm text-[#1D2A22] placeholder:text-[#7A8276]"
                />
              </label>
              {showResults && results.length > 0 && (
                <ul className="border-t border-[#E5E1D8] max-h-[280px] overflow-y-auto">
                  {results.map((c) => (
                    <li key={c.naam}>
                      <button
                        onClick={() => pickCity(c)}
                        data-testid={`kaart-result-${c.naam}`}
                        className="w-full text-left px-4 py-3 hover:bg-[#EFECE5] transition-colors flex items-center gap-3"
                      >
                        <MapPin size={14} className="text-[#E06B3F] shrink-0" />
                        <div>
                          <p className="text-sm text-[#1D2A22]">{c.naam}</p>
                          <p className="text-[11px] text-[#7A8276]">{c.prov} · {fmt(c.bijstand)}</p>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
        <p className="mt-3 text-[11px] text-[#7A8276] leading-relaxed">
          Bron: CBS StatLine 82015NED (bijstandsuitkeringen, dec-2024). Aantallen
          per stad zijn indicatief en dienen als richtcijfer.
        </p>
      </div>

      {/* SIDE, selected details */}
      <div className="lg:col-span-4 space-y-4 lg:sticky lg:top-28">
        <div className="rounded-2xl bg-[#1D2A22] text-[#FAF7F1] p-6 sm:p-7">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#D19B72] mb-3">
            Landelijk actief
          </p>
          <p className="font-heading text-5xl leading-none">397.820</p>
          <p className="mt-3 text-sm text-[#FAF7F1]/70 leading-relaxed">
            personen in de bijstand, LevelLeven bouwt fundament, gemeente per
            gemeente.
          </p>
        </div>

        <div
          data-testid="kaart-stad-detail"
          className="rounded-2xl border border-[#E5E1D8] bg-[#FAF7F1] p-6 sm:p-7 min-h-[220px]"
        >
          {selected ? (
            <>
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#E06B3F] mb-2">
                {selected.prov}
              </p>
              <p className="font-heading text-3xl text-[#1D2A22] leading-tight">
                {selected.naam}
              </p>
              <p className="mt-4 text-xs text-[#7A8276]">
                Bijstandsuitkeringen (indicatief)
              </p>
              <p className="font-heading text-4xl text-[#E06B3F] mt-1">
                {fmt(selected.bijstand)}
              </p>
              <Link
                to={`/contact?rol=wachtlijst&stad=${encodeURIComponent(selected.naam)}&provincie=${encodeURIComponent(selected.prov)}`}
                data-testid="kaart-wachtlijst-cta"
                className="mt-6 group inline-flex items-center gap-3 bg-[#1D2A22] hover:bg-[#E06B3F] text-white rounded-full pl-5 pr-2 py-2 transition-colors text-sm"
              >
                <span>Plaats mij op de wachtlijst, {selected.naam}</span>
                <span className="bg-[#E06B3F] group-hover:bg-white group-hover:text-[#1D2A22] text-white w-8 h-8 rounded-full flex items-center justify-center transition-colors">
                  <ArrowUpRight size={14} />
                </span>
              </Link>
            </>
          ) : (
            <>
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#7A8276] mb-3">
                Selecteer een stad
              </p>
              <p className="font-heading text-2xl text-[#1D2A22] leading-snug">
                Zoek uw stad of tik op een cirkel. Wij zijn overal inzetbaar,
                óók buiten de standaard pilot-regio's, via de wachtlijst.
              </p>
              <p className="mt-4 text-sm text-[#4A5D4E]">
                Cirkelgrootte = aantal bijstandsuitkeringen in de stad.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
